import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import api from "../../../services/api";

const SizeFormModal = ({ onClose, onSave, size }) => {
  const [formData, setFormData] = useState({
    displayName: "",
    name: "",
    price: "",
    servings: "",
    priceType: "",
    tierCount: "",
    category: "",
    visibility: "public",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (size) {
      setFormData({
        ...size,
        price: String(size.price),
        servings: String(size.servings),
        priceType: size.priceType || "",
        tierCount: size.tierCount || "",
        category: size.category || "",
        visibility: size.visibility || "public",
      });
    }
  }, [size]);

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const validate = () => {
    const newErrors = {};
    if (!formData.displayName.trim()) newErrors.displayName = "Required";
    if (!formData.name.trim()) newErrors.name = "Required";
    if (formData.price === "") newErrors.price = "Required";
    if (formData.servings === "") newErrors.servings = "Required";
    if (!formData.priceType) newErrors.priceType = "Required";
    if (formData.category === "specialty" && !formData.tierCount) {
      newErrors.tierCount = "Required for specialty sizes";
    }
    if (!formData.category) newErrors.category = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload = {
        ...formData,
        price: Number(formData.price),
        servings: Number(formData.servings),
        tierCount: Number(formData.tierCount),
      };

      if (size) {
        await api.put(`/sizes/${size._id}`, payload);
      } else {
        await api.post("/sizes", payload);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving size:", error);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{size ? "Edit Size" : "Add New Size"}</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Display Name"
          fullWidth
          value={formData.displayName}
          onChange={handleChange("displayName")}
          error={!!errors.displayName}
          helperText={errors.displayName}
          required
        />
        <TextField
          margin="dense"
          label="Internal Name"
          fullWidth
          value={formData.name}
          onChange={handleChange("name")}
          error={!!errors.name}
          helperText={errors.name}
          required
        />
        <TextField
          margin="dense"
          label="Price"
          type="number"
          fullWidth
          value={formData.price}
          onChange={handleChange("price")}
          error={!!errors.price}
          helperText={errors.price}
          required
        />
        <TextField
          margin="dense"
          label="Servings"
          type="number"
          fullWidth
          value={formData.servings}
          onChange={handleChange("servings")}
          error={!!errors.servings}
          helperText={errors.servings}
          required
        />

        <FormControl
          fullWidth
          required
          sx={{ mt: 2 }}
          error={!!errors.priceType}>
          <InputLabel>Price Type</InputLabel>
          <Select
            value={formData.priceType}
            label="Price Type"
            onChange={handleChange("priceType")}>
            <MenuItem value="absolute">Absolute</MenuItem>
            <MenuItem value="modifier">Modifier</MenuItem>
          </Select>
          {errors.priceType && (
            <FormHelperText>{errors.priceType}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          required
          sx={{ mt: 2 }}
          error={!!errors.tierCount}>
          <InputLabel>Tier Count</InputLabel>
          <Select
            value={formData.tierCount}
            label="Tier Count"
            onChange={handleChange("tierCount")}>
            {[1, 2, 3].map((tier) => (
              <MenuItem key={tier} value={tier}>
                {tier}
              </MenuItem>
            ))}
          </Select>
          {errors.tierCount && (
            <FormHelperText>{errors.tierCount}</FormHelperText>
          )}
        </FormControl>

        <FormControl
          fullWidth
          required
          sx={{ mt: 2 }}
          error={!!errors.category}>
          <InputLabel>Category</InputLabel>
          <Select
            value={formData.category}
            label="Category"
            onChange={handleChange("category")}>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="specialty">Specialty</MenuItem>
          </Select>
          {errors.category && (
            <FormHelperText>{errors.category}</FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={formData.visibility}
            label="Visibility"
            onChange={handleChange("visibility")}>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="admin">Admin Only</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SizeFormModal;
